import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import pmmService from '../services/paymentMigrationManagerService';
import OhipLovPicker from './OhipAgentTypePicker';
import AuthorizationRuleFields from './AuthorizationRuleFields';
import './AddCorporateProfilePage.css';

const ROUTING_TYPES = [
    { value: 'Window Routing', label: 'Window Routing' },
    { value: 'Room Routing',   label: 'Room Routing' },
];

const WINDOWS = ['1', '2', '3', '4', '5', '6', '7', '8'];

const PMMAddRoutingPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [form, setForm] = useState({ routingType: 'Window Routing', defaultWindow: '1', routingCode: '', description: '', authorizationRuleCode: '' });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        setErrors(e => ({ ...e, [field]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.routingType) e.routingType = 'Routing Type is required';
        if (!form.defaultWindow) e.defaultWindow = 'Default Window is required';
        if (!form.routingCode.trim()) e.routingCode = 'Routing Code is required';
        if (!form.description.trim()) e.description = 'Description is required';
        return e;
    };

    const handleCreate = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setSaving(true);
        try {
            await pmmService.createRouting(id, {
                code:                    form.routingCode,
                type:                    form.routingType || null,
                description:             form.description || null,
                default_window:          form.defaultWindow || null,
                authorization_rule_code: form.authorizationRuleCode || null,
            });
            navigate(`/apps/payment-migration-manager/${id}`);
        } catch (err) {
            setErrors({ submit: err.response?.data?.error?.message || 'Failed to save routing' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="acp-page">
            <div className="acp-card">
                <div className="acp-card-header">
                    <h1 className="acp-title">Add Routing</h1>
                </div>
                <div className="acp-divider" />
                <div className="acp-body">
                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">Routing Type <span className="acp-required">*</span></label>
                            <select className={`acp-select${errors.routingType ? ' acp-input-error' : ''}`} value={form.routingType} onChange={e => set('routingType', e.target.value)} autoFocus>
                                {ROUTING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                            {errors.routingType && <span className="acp-error-msg">{errors.routingType}</span>}
                        </div>
                        <div className="acp-field">
                            <label className="acp-label">Default Window <span className="acp-required">*</span></label>
                            <select className={`acp-select${errors.defaultWindow ? ' acp-input-error' : ''}`} value={form.defaultWindow} onChange={e => set('defaultWindow', e.target.value)}>
                                {WINDOWS.map(w => <option key={w} value={w}>{w}</option>)}
                            </select>
                            {errors.defaultWindow && <span className="acp-error-msg">{errors.defaultWindow}</span>}
                        </div>
                    </div>
                    <div className="acp-row-2">
                        {/* No amount field: v2_pmm_routing has no authorization_amount column, so the
                            shared component renders the code on its own. */}
                        <AuthorizationRuleFields
                            code={form.authorizationRuleCode}
                            onCodeChange={v => set('authorizationRuleCode', v)}
                        />
                    </div>
                    <div className="acp-field">
                        <label className="acp-label">Routing Code <span className="acp-required">*</span></label>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                            <input className={`acp-input${errors.routingCode ? ' acp-input-error' : ''}`} type="text" placeholder="Enter routing code" value={form.routingCode} onChange={e => set('routingCode', e.target.value)} />
                            <OhipLovPicker
                                fetchFn={() => pmmService.getOhipRoutingCodes(id)}
                                title="Select Routing Instruction from Opera Cloud"
                                emptyMsg="No routing instructions returned from Opera Cloud."
                                onSelectItem={({ code, description }) => {
                                    set('routingCode', code);
                                    if (description && description !== code) set('description', description);
                                }}
                            />
                        </div>
                        {errors.routingCode && <span className="acp-error-msg">{errors.routingCode}</span>}
                    </div>
                    <div className="acp-field">
                        <label className="acp-label">Description <span className="acp-required">*</span></label>
                        <textarea className={`acp-input${errors.description ? ' acp-input-error' : ''}`} rows={4} placeholder="Describe the routing code and its purpose" value={form.description} onChange={e => set('description', e.target.value)} style={{ resize: 'vertical' }} />
                        {errors.description && <span className="acp-error-msg">{errors.description}</span>}
                    </div>
                </div>
                {errors.submit && <div className="acp-error-msg" style={{ margin: '0 0 1rem' }}>{errors.submit}</div>}
                <div className="acp-footer">
                    <button className="acp-btn-cancel" onClick={() => navigate(`/apps/payment-migration-manager/${id}`)} disabled={saving}>Cancel</button>
                    <button className="acp-btn-create" onClick={handleCreate} disabled={saving}>{saving ? 'Creating...' : 'Create'}</button>
                </div>
            </div>
        </div>
    );
};

export default PMMAddRoutingPage;
